import React, { useState, useEffect, useMemo } from 'react'
import { FormControl, Alert, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import useStores from '../../stores/useStore'
import { debounce } from 'throttle-debounce'
import AssetGroupItem from './AssetGroupItem'
import SearchResultAmount from '../Forms/SearchResultAmount'
import TopLevelTreeItem from '../Offices/TopLevelTreeItem'
import { emptyGuid, isEmptyGuid } from '../../utils/emptyGuid'
import { observer } from 'mobx-react'
import NoDataAlert from '../Configuration/NoDataAlert'

const StyledSearchWrap = styled.div`
  margin-bottom: 2rem;
`

const StyledShowMore = styled.span`
  color: ${(props) => props.theme.primaryColor};
  float: right;
  margin-top: 15px;
  cursor: pointer;
`

const AssetGroupsTree = ({
  onClick,
  isClickable,
  withSearch,
  renderExtraItemContent = () => null,
  parentsWithSelectedChildren,
  getContextMenuOptions,
  showTopLevel,
}) => {
  const [filter, setFilter] = useState('')
  const [filterIsLoading, setFilterIsLoading] = useState(false)
  const [filteredGroups, setFilteredGroups] = useState([])
  const [hasSearched, setHasSearched] = useState(false)
  const [groupsActive, setGroupsActive] = useState({})
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [limit, setLimit] = useState(true)
  const limitLength = 40
  const { t } = useTranslation()
  const { assetGroupStore } = useStores()

  // Observables from store
  // const partialGroups = useObserver(() => assetGroupStore.loadedGroups)
  // const error = useObserver(() => assetGroupStore.error)
  const partialGroups = assetGroupStore.loadedGroups
  const error = assetGroupStore.error

  // Effect to load groups
  useEffect(() => {
    assetGroupStore.getGroups(emptyGuid)
  }, [assetGroupStore])

  // Effect to expand child groups when toggled
  useEffect(() => {
    if (Object.keys(groupsActive).length) {
      for (const groupId of Object.keys(groupsActive)) {
        if (groupsActive[groupId]) {
          assetGroupStore.getGroups(groupId)
        }
      }
    }
  }, [assetGroupStore, groupsActive])

  useEffect(() => {
    return () => {
      assetGroupStore.clearError() // Clear the error when the component unmounts
    }
  }, [assetGroupStore])

  // Debounced filter change for searching groups
  const loadFilteredGroupsDebounced = useMemo(
    () =>
      debounce(500, async (value) => {
        if (!value) {
          setHasSearched(false)
          assetGroupStore.cancelGetFilteredGroups()
          setFilteredGroups([])
          setLimit(false)
          return
        }
        try {
          setHasSearched(false)
          setLimit(true)
          setFilteredGroups([])
          setFilterIsLoading(true)
          const loadedGroups = await assetGroupStore.getFilteredGroups(value)
          setFilteredGroups(loadedGroups)
          setHasSearched(true)
        } finally {
          setFilterIsLoading(false)
        }
      }),
    [assetGroupStore]
  )

  const handleChangeFilter = (e) => {
    const { value } = e.target
    setFilter(value)
    loadFilteredGroupsDebounced(value)
  }

  // Computed: Top-level groups for rendering
  const topLevelGroups = useMemo(() => {
    if (filter) {
      return filteredGroups
    }
    return partialGroups.filter((group) =>
      isEmptyGuid(group.parent?.id ?? emptyGuid)
    )
  }, [partialGroups, filter, filteredGroups])

  const limitedTopLevelGroups = useMemo(() => {
    if (limit) {
      return topLevelGroups.slice(0, limitLength)
    }
    return topLevelGroups
  }, [limit, topLevelGroups])

  return (
    <>
      {withSearch && (partialGroups.length > 0 || filter) && (
        <StyledSearchWrap>
          <FormControl
            type='input'
            placeholder={t('assetGroups:searchGroup')}
            onChange={handleChangeFilter}
            value={filter}
          />
          {filter && <SearchResultAmount count={topLevelGroups.length} />}
        </StyledSearchWrap>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}
      {showTopLevel ? (
        <TopLevelTreeItem
          handleClick={onClick}
          renderExtraItemContent={renderExtraItemContent}
        >
          {limitedTopLevelGroups.map((group) => (
            <AssetGroupItem
              key={group.id}
              group={group}
              setGroupsActive={setGroupsActive}
              onClick={onClick}
              partialGroups={partialGroups}
              isClickable={isClickable}
              groupsActive={groupsActive}
              renderExtraItemContent={renderExtraItemContent}
              parentsWithSelectedChildren={parentsWithSelectedChildren}
              selectedGroupId={selectedGroupId}
              setSelectedGroupId={setSelectedGroupId}
              getContextMenuOptions={getContextMenuOptions}
            />
          ))}
        </TopLevelTreeItem>
      ) : (
        limitedTopLevelGroups.map((group) => (
          <AssetGroupItem
            key={group.id}
            group={group}
            setGroupsActive={setGroupsActive}
            onClick={onClick}
            partialGroups={partialGroups}
            isClickable={isClickable}
            groupsActive={groupsActive}
            renderExtraItemContent={renderExtraItemContent}
            parentsWithSelectedChildren={parentsWithSelectedChildren}
            selectedGroupId={selectedGroupId}
            setSelectedGroupId={setSelectedGroupId}
            getContextMenuOptions={getContextMenuOptions}
          />
        ))
      )}
      {topLevelGroups.length > limitLength && limit && (
        <div className='d-flex justify-content-center'>
          <StyledShowMore onClick={() => setLimit(false)}>
            {t('assetGroups:showAll')}
          </StyledShowMore>
        </div>
      )}
      {(assetGroupStore.groupsLoading || filterIsLoading) && (
        <div className='d-flex justify-content-center'>
          <Spinner animation='border' variant='primary' />
        </div>
      )}
      {!filter &&
        partialGroups.length === 0 &&
        !assetGroupStore.groupsLoading && (
          <NoDataAlert text={t('administration:noDataEntered')} />
        )}
      {filter &&
        hasSearched &&
        topLevelGroups.length === 0 &&
        !filterIsLoading && (
          <div className='ps-4 pe-2'>
            <strong>{t('assetGroups:searchStructureTreeNoresult')}</strong>
          </div>
        )}
    </>
  )
}

export default observer(AssetGroupsTree)
